import { AppState } from '@chegg-wt/core/state'
import { sanitizeHtmlWithFilter } from '@chegg-wt/core/utils/contentFilters'
import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'

import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import {
  origins,
  UserClickPayload,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import Checkbox from '@chegg-wt/core/components/Checkbox'
import { GATED_IN_TEXT_CITATION_MODAL } from '@chegg-wt/core/state/modals/constants'
import { GatedInTextCitationModalData } from '@chegg-wt/core/state/bibliography/types'
import { OpenModalPayload } from '@chegg-wt/core/state/modals/types'
import { Config } from '@chegg-wt/core/config'
import { Citation as CitationAPICitation } from '@chegg-wt/core/types/redux-citation-api'
import {
  deleteCitations,
  editCitation,
  toggleCitationCheckbox,
} from '@chegg-wt/core/state/bibliography/actions'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import { footnoteStyleSelector } from '@chegg-wt/core/state/currentProject/selectors'
import { isProSelector } from '@chegg-wt/core/state/user/selectors'
import Validator from './Validator'
import * as Styled from './styled'
import Notes from './Notes'
import Menu from './Menu'

interface Props {
  isFootnote: boolean
  isPro: boolean
  citation: CitationAPICitation
  toggleCitationCheckbox: (id: string) => void
  editCitation: (citation: CitationAPICitation) => void
  deleteCitations: (citations: CitationAPICitation[]) => void
  copyCitation: (citation: CitationAPICitation) => void
  copyPremiumCitation: (citation: CitationAPICitation) => void
  openModal: (params: OpenModalPayload) => void
  isSelected?: boolean
  userClick: (payload: UserClickPayload) => void
}

export const Citation: React.FC<Props> = ({
  isFootnote,
  isPro,
  citation,
  toggleCitationCheckbox,
  editCitation,
  deleteCitations,
  copyCitation,
  copyPremiumCitation,
  isSelected,
  openModal,
  userClick,
}) => {
  const [showNotes, toggleNotes] = useState(false)
  const citationText = useRef(null)
  const buttonContainer = useRef<HTMLDivElement>(null)
  const { id, selected, isIncomplete, isNew, formatted_data } = citation
  // TODO: this is a bit hacky. window from dompurify is undefined in SSR. find alternative library.
  useEffect(() => {
    citationText.current.innerHTML = sanitizeHtmlWithFilter(formatted_data.output)
  }, [formatted_data.output])

  const handleCopyClick = () => {
    copyCitation(citation)
  }

  const handleCopyPremiumClick = () => {
    if (Config.gateInTextCitations && !isPro) {
      openModal({
        name: GATED_IN_TEXT_CITATION_MODAL,
        data: {
          isFootnote,
        } as GatedInTextCitationModalData,
      })
      return
    }
    copyPremiumCitation(citation)
  }

  const handleReviewCitationClick = () => {
    editCitation(citation)
    userClick({ origin: origins.reviewCitationClick })
  }

  const copyMenuPremiumCitations = () => {
    userClick({ origin: origins.copyInTextCitationMenuCTA })
    handleCopyPremiumClick()
  }

  return (
    <Styled.CitationContainer isIncomplete={isIncomplete} isNew={isNew}>
      <Styled.CitationWrapper>
        <Styled.CheckboxWrapper>
          <Checkbox
            id={id}
            selected={isSelected || selected}
            onChange={() => toggleCitationCheckbox(id)}
            label="select citation"
          />
        </Styled.CheckboxWrapper>
        <Styled.VisibleOnHoverWrapper dismissible={buttonContainer}>
          <Styled.SpacedText tabIndex={0}>
            <Styled.CitationText ref={citationText} data-test="citation-text" />
            <Styled.DismissibleButtons ref={buttonContainer}>
              <Styled.Buttons>
                <Styled.CopyContainer>
                  <Styled.CopyButton
                    size="small"
                    data-test="copy-bibliography-citation"
                    onClick={() => {
                      handleCopyClick()
                      userClick({ origin: origins.copyBibliographyCitationClick })
                    }}
                  >
                    Copy citation
                  </Styled.CopyButton>
                </Styled.CopyContainer>
                <Styled.InTextCopyContainer>
                  <Styled.CopyButton
                    size="small"
                    data-test="copy-in-text-citation"
                    onClick={() => {
                      userClick({ origin: origins.copyInTextCitationHoverCTA })
                      handleCopyPremiumClick()
                    }}
                  >
                    {isFootnote ? 'Copy footnote' : 'Copy in-text citation'}
                  </Styled.CopyButton>
                </Styled.InTextCopyContainer>
              </Styled.Buttons>
            </Styled.DismissibleButtons>
          </Styled.SpacedText>
        </Styled.VisibleOnHoverWrapper>
        <Menu
          citation={citation}
          editCitation={editCitation}
          deleteCitations={deleteCitations}
          isFootnote={isFootnote}
          copyCitations={handleCopyClick}
          copyPremiumCitations={copyMenuPremiumCitations}
          openNotes={toggleNotes}
          userClick={userClick}
        />
      </Styled.CitationWrapper>
      {isIncomplete && <Validator onClickReview={handleReviewCitationClick} id={id} />}
      {showNotes && (
        <Notes
          citationId={citation.id}
          projectId={citation.projectId}
          toggleNotes={toggleNotes}
        />
      )}
    </Styled.CitationContainer>
  )
}

const mapStateToProps = (state: AppState) => ({
  isFootnote: footnoteStyleSelector(state),
  isPro: isProSelector(state),
})

const mapDispatchToProps = {
  toggleCitationCheckbox,
  openModal,
  editCitation,
  deleteCitations,
  userClick,
}

export default connect(mapStateToProps, mapDispatchToProps)(Citation)
